import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatNumber, getSortIndicator } from '../../../../../utils/helpers';
import useSortedData from '../../../hooks/useSortedData';
import usePagination from '../../../hooks/usePagination';
import './PlayerPicker.css';

const ITEMS_PER_PAGE = 25;

const PlayerPickerSnake = ({ players, onStagePlayer, stagedPlayer, disabled, sport }) => {
  const isGolf = sport.toLowerCase() === 'golf';

  const defaultSortKey = isGolf 
    ? 'salary' 
    : 'sportSpecificData.icmValue';

  const { sortedData: sortedPlayers, requestSort, sortConfig } = useSortedData(
    players,
    defaultSortKey,
    'descending'
  );

  const {
    currentItems,
    nextPage,
    prevPage,
    currentPage,
    maxPage,
    resetPage,
  } = usePagination(sortedPlayers, ITEMS_PER_PAGE);

  useEffect(() => {
    resetPage();
  }, [sortConfig, resetPage]);

  const handleStagePlayer = (player) => {
    if (disabled) return;
    onStagePlayer(player);
  };

  const renderPokerHeaders = () => (
    <>
      <th onClick={() => requestSort('sportSpecificData.chipCount')}>
        CHIPS {getSortIndicator(sortConfig, 'sportSpecificData.chipCount')}
      </th>
      <th onClick={() => requestSort('sportSpecificData.icmValue')}>
        ICM VALUE {getSortIndicator(sortConfig, 'sportSpecificData.icmValue')}
      </th>
    </>
  );

  const renderGolfHeaders = () => (
    <>
      <th onClick={() => requestSort('sportSpecificData.odds')}>
        ODDS {getSortIndicator(sortConfig, 'sportSpecificData.odds')}
      </th>
      <th onClick={() => requestSort('salary')}>
        SALARY {getSortIndicator(sortConfig, 'salary')}
      </th>
    </>
  );

  const renderPokerRow = (player) => (
    <>
      <td>{formatNumber(player.sportSpecificData?.chipCount)}</td>
      <td>${formatNumber(player.sportSpecificData?.icmValue)}</td>
    </>
  );

  const renderGolfRow = (player) => (
    <>
      <td>{player.sportSpecificData?.odds}</td>
      <td>${formatNumber(player.salary)}</td>
    </>
  );

  return (
    <div className="player-picker-container">
      <div className="player-picker-table-header">
        <span className="player-picker-table-header-text">
          AVAILABLE PLAYERS
        </span>
      </div>
      <table className="player-picker-table">
        <thead>
          <tr>
            <th onClick={() => requestSort('name')}>
              NAME {getSortIndicator(sortConfig, 'name')}
            </th>
            {isGolf ? renderGolfHeaders() : renderPokerHeaders()}
          </tr>
        </thead>
        <tbody>
          {currentItems.map((player) => (
            <tr 
              key={player.name} 
              className={`player-row ${stagedPlayer?.name === player.name ? 'staged' : ''} ${disabled ? 'disabled' : ''}`}
              onClick={() => handleStagePlayer(player)}
            >
              <td>{player.name}</td>
              {isGolf ? renderGolfRow(player) : renderPokerRow(player)}
            </tr>
          ))}
        </tbody>
      </table>
      {players.length > ITEMS_PER_PAGE && (
        <div className="pagination-controls">
          <button onClick={prevPage} className="pagination-btn" disabled={currentPage === 1}>
            &lt; Prev
          </button>
          <span className="pagination-info">
            Page {currentPage} of {maxPage}
          </span>
          <button onClick={nextPage} className="pagination-btn" disabled={currentPage === maxPage}>
            Next &gt;
          </button>
        </div>
      )}
    </div>
  );
};

PlayerPickerSnake.propTypes = {
  players: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    salary: PropTypes.number,
    sportSpecificData: PropTypes.shape({
      chipCount: PropTypes.number,
      icmValue: PropTypes.number,
      odds: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  })).isRequired,
  onStagePlayer: PropTypes.func.isRequired,
  stagedPlayer: PropTypes.shape({
    name: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  sport: PropTypes.string.isRequired,
};

PlayerPickerSnake.defaultProps = {
  disabled: false,
  stagedPlayer: null,
};

export default PlayerPickerSnake;
